import { useState } from 'react'
import { Drawer, Chip, Button } from '@/components/ui'
import { CheckCircle2, AlertTriangle, ChevronDown, Download, MessagesSquare, RotateCw } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Ejecucion {
  id: string
  fecha: string
  estado: 'ok' | 'errores' | 'pendiente'
  tanda: string
  enviados: number
  entregados: number
  leidos: number
  errores: number
  respondieron: number
  destinatarios?: {
    cliente: string
    whatsapp: string
    estado: 'entregado' | 'leido' | 'leido_respondio' | 'error'
    wamid: string
  }[]
  erroresDetalle?: {
    cliente: string
    codigo: string
    mensaje: string
    accion: string
  }[]
}

const ejecucionesMock: Ejecucion[] = [
  {
    id: 'ej-hoy-15',
    fecha: 'Hoy 15:00',
    estado: 'ok',
    tanda: 'Tanda 2 de 2',
    enviados: 55, entregados: 53, leidos: 41, errores: 0, respondieron: 8,
    destinatarios: [
      { cliente: 'MIELES CEDEÑO LIGIA', whatsapp: '+593 99 XXX 2466', estado: 'leido_respondio', wamid: 'wamid.HBg...8QA' },
      { cliente: 'GREFA SALAZAR DANNY', whatsapp: '+593 99 XXX 7640', estado: 'leido', wamid: 'wamid.HBg...2fA' },
      { cliente: 'GAVICA MERO SUSAN', whatsapp: '+593 99 XXX 4426', estado: 'entregado', wamid: 'wamid.HBg...4bA' },
      { cliente: 'RODRIGUEZ ENGRACIA V.', whatsapp: '+593 99 XXX 1288', estado: 'leido_respondio', wamid: 'wamid.HBg...9kA' },
      { cliente: 'VERA LOOR PEDRO', whatsapp: '+593 99 XXX 3321', estado: 'entregado', wamid: 'wamid.HBg...5zA' },
    ],
  },
  {
    id: 'ej-hoy-10',
    fecha: 'Hoy 10:00',
    estado: 'errores',
    tanda: 'Tanda 1 de 2',
    enviados: 55, entregados: 52, leidos: 38, errores: 3, respondieron: 6,
    erroresDetalle: [
      { cliente: 'CEDEÑO LUIS', codigo: '131026', mensaje: 'Recipient not WhatsApp user', accion: 'Marcar celular inválido' },
      { cliente: 'LOOR SANDRA', codigo: '131056', mensaje: 'Rate limit excedido', accion: 'Reintentar en 1h' },
      { cliente: 'INTRIAGO JORGE', codigo: '131021', mensaje: 'Message undeliverable', accion: 'Verificar número' },
    ],
  },
  {
    id: 'ej-ayer-15',
    fecha: 'Ayer 15:00',
    estado: 'ok',
    tanda: 'Tanda 2 de 2',
    enviados: 55, entregados: 54, leidos: 42, errores: 0, respondieron: 12,
  },
  {
    id: 'ej-ayer-10',
    fecha: 'Ayer 10:00',
    estado: 'ok',
    tanda: 'Tanda 1 de 2',
    enviados: 55, entregados: 55, leidos: 48, errores: 0, respondieron: 9,
  },
]

const estadoChip = {
  entregado: { label: '✓ entregado', color: 'bg-slate-100 text-slate-600' },
  leido: { label: '✓✓ leído', color: 'bg-blue-50 text-blue-700' },
  leido_respondio: { label: '✓✓ leído · respondió', color: 'bg-emerald-50 text-emerald-700' },
  error: { label: '✗ error', color: 'bg-rose-50 text-rose-700' },
}

interface Props {
  open: boolean
  onClose: () => void
  campanaNombre: string
}

export function HistorialDrawer({ open, onClose, campanaNombre }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['ej-hoy-15']))
  const [filtro, setFiltro] = useState<'hoy' | '7d' | 'all'>('hoy')

  function toggle(id: string) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={campanaNombre}
      subtitle="Historial de ejecuciones"
      width={820}
    >
      <div className="space-y-5 px-6 py-5">
        <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-4 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Tandas totales</div>
            <div className="font-mono text-xl font-semibold text-navy-700 mt-1">148</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Mensajes enviados</div>
            <div className="font-mono text-xl font-semibold text-navy-700 mt-1">1,100</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Tasa entrega</div>
            <div className="font-mono text-xl font-semibold text-emerald-600 mt-1">97.3%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Respondieron</div>
            <div className="font-mono text-xl font-semibold text-emerald-600 mt-1">287 <span className="text-xs text-slate-400">(26%)</span></div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Filtrar:</span>
            {[
              { k: 'hoy' as const, l: 'Hoy' },
              { k: '7d' as const, l: 'Últimos 7d' },
              { k: 'all' as const, l: 'Todas' },
            ].map(opt => (
              <button
                key={opt.k}
                onClick={() => setFiltro(opt.k)}
                className={cn(
                  'px-3 py-1 rounded-md text-xs font-medium transition',
                  filtro === opt.k
                    ? 'bg-navy-700 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                )}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        </div>

        <div className="space-y-3">
          {ejecucionesMock.map(ej => {
            const isExp = expanded.has(ej.id)
            const isOk = ej.estado === 'ok'
            const isErr = ej.estado === 'errores'
            return (
              <article
                key={ej.id}
                className={cn(
                  'rounded-xl border bg-white overflow-hidden',
                  isOk && 'border-slate-200',
                  isErr && 'border-amber-300'
                )}
              >
                <button
                  onClick={() => toggle(ej.id)}
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-4 py-3 text-left',
                    isErr && 'hover:bg-amber-50/40'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-9 h-9 rounded-full flex items-center justify-center',
                      isOk ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                    )}>
                      {isOk ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="font-semibold text-navy-700 text-sm flex items-center gap-2">
                        {ej.fecha}
                        <Chip variant={isOk ? 'emerald' : 'amber'}>
                          {isOk ? 'Completada' : 'Con errores'}
                        </Chip>
                        <Chip variant="navy">{ej.tanda}</Chip>
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        {ej.enviados} enviados · {ej.entregados} entregados ·{' '}
                        <span className={cn(ej.errores > 0 && 'font-bold text-amber-700')}>
                          {ej.errores} errores
                        </span>{' '}
                        · {ej.respondieron} respondieron
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isErr && (
                      <Button variant="amber" size="sm" onClick={e => e.stopPropagation()}>
                        <RotateCw className="w-3.5 h-3.5" /> Reintentar {ej.errores} fallidos
                      </Button>
                    )}
                    <ChevronDown className={cn('w-4 h-4 text-slate-400 transition', isExp && 'rotate-180')} />
                  </div>
                </button>

                {isExp && (
                  <div className={cn('border-t px-4 py-4 space-y-4', isErr ? 'border-amber-100 bg-amber-50/30' : 'border-slate-100 bg-slate-50/50')}>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { l: 'Enviados', v: ej.enviados, c: 'text-navy-700' },
                        { l: 'Entregados', v: ej.entregados, c: 'text-emerald-600' },
                        { l: 'Leídos', v: ej.leidos, c: 'text-blue-600' },
                        { l: 'Respondieron', v: ej.respondieron, c: 'text-emerald-600' },
                      ].map(s => (
                        <div key={s.l} className="bg-white rounded-lg p-3 border border-slate-200">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.l}</div>
                          <div className={cn('font-mono font-semibold text-lg mt-0.5', s.c)}>{s.v}</div>
                        </div>
                      ))}
                    </div>

                    {ej.erroresDetalle && (
                      <div>
                        <div className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" /> Errores detectados
                        </div>
                        <div className="overflow-hidden rounded-lg border border-amber-200 bg-white">
                          <table className="w-full text-xs">
                            <thead className="bg-amber-50 text-amber-800">
                              <tr>
                                <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                                <th className="text-left px-3 py-2 font-semibold">Error Meta</th>
                                <th className="text-left px-3 py-2 font-semibold">Acción sugerida</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100">
                              {ej.erroresDetalle.map((e, i) => (
                                <tr key={i}>
                                  <td className="px-3 py-2 font-medium text-navy-700">{e.cliente}</td>
                                  <td className="px-3 py-2 font-mono text-slate-600">({e.codigo}) {e.mensaje}</td>
                                  <td className="px-3 py-2 text-slate-600">{e.accion}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {ej.destinatarios && (
                      <div>
                        <div className="text-xs font-semibold text-slate-700 mb-2">Destinatarios (primeros 5 de {ej.enviados})</div>
                        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-100">
                              <tr className="text-slate-600">
                                <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                                <th className="text-left px-3 py-2 font-semibold">WhatsApp</th>
                                <th className="text-left px-3 py-2 font-semibold">Estado</th>
                                <th className="text-left px-3 py-2 font-semibold">wamid</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {ej.destinatarios.map((d, i) => {
                                const e = estadoChip[d.estado]
                                return (
                                  <tr key={i}>
                                    <td className="px-3 py-2 font-medium text-navy-700">{d.cliente}</td>
                                    <td className="px-3 py-2 font-mono text-slate-600">{d.whatsapp}</td>
                                    <td className="px-3 py-2">
                                      <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium', e.color)}>
                                        {e.label}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{d.wamid}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button variant="outline" size="sm">
                            <Download className="w-3.5 h-3.5" /> Export CSV completo
                          </Button>
                          <Button variant="outline" size="sm">
                            <MessagesSquare className="w-3.5 h-3.5" /> Ver conversaciones
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </article>
            )
          })}
        </div>

        <div className="text-center">
          <button className="text-xs text-slate-500 hover:text-navy-700">
            Cargar más ejecuciones ↓
          </button>
        </div>
      </div>
    </Drawer>
  )
}
